import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useEffect, useState } from 'react'
import { autofill, change } from 'redux-form'
import omit from 'lodash/omit'
import pick from 'lodash/pick'
import { CREATE_MULTIPLE_ACCOUNTS, CREATE_NEW_ACCOUNT } from 'modules/Services/types'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'

export const useAccountCreateFormSubmit = ({ form: formName, onClose, multiLogins = false, networkType }) => {
  const dispatch = useDispatch()
  const [submitted, setSubmitted] = useState(false)

  const {
    values = {},
    registeredFields = {},
    anyTouched = false,
    submitSucceeded = false,
  } = useSelector(({ form }) => form[formName])

  const [accountConfig, setAccountConfig] = useLocalStorage('CreateAccountConfig', {})
  const [, setAdvertiserPresetConfig] = useLocalStorage('CreateAccountAdvertiserPresetConfig', {})
  const [, setFillingTypeConfig] = useLocalStorage('CreateAccountFillingTypeConfig', {})

  //need to send data from currently registered form fields only!
  const registerFieldsNames = Object.keys(registeredFields)

  useEffect(() => {
    setAccountConfig({ anyTouched, submitSucceeded })
  }, [anyTouched, onClose, submitSucceeded, setAccountConfig])

  useEffect(() => {
    if (submitted) {
      setSubmitted(false)
    }

    setAccountConfig({
      [formName]: omit(values, ['login', 'logins', 'network']),
    })
  }, [values])

  const sendMessageToYandex = useCallback(() => {
    if (global.yaCounter32793525) {
      global.yaCounter32793525.reachGoal('new-network-account')
      global.yaCounter32793525.reachGoal(`${networkType}-register`)
    }
    global.dataLayer && global.dataLayer.push({ event: 'new-network-account' })
  }, [])

  useEffect(() => {
    Object.entries(accountConfig?.[formName] ?? {}).forEach(([key, value]) => {
      dispatch(autofill(formName, key, value))
    })
  }, [])

  const submitData = useCallback(
    (values) => {
      const updateLoginsState = (response) =>
        Object.entries(response).forEach(([i, { created = false }]) => {
          dispatch(
            change(
              formName,
              `logins[${i}]`,
              {
                ...values.logins[i],
                created,
              },
              false,
              true,
            ),
          )
          if (created) {
            sendMessageToYandex()
          }
        })

      return multiLogins
        ? new Promise((resolve, reject) =>
            dispatch({
              meta: { resolve, reject },
              type: CREATE_MULTIPLE_ACCOUNTS,
              payload: {
                data: {
                  ...pick(values, registerFieldsNames),
                  logins: values.logins.filter(({ created }) => !created),
                },
                formName,
              },
            }),
          )
            .then(updateLoginsState)
            .catch(({ accountStates, error }) => {
              updateLoginsState(accountStates)
              throw error
            })
            .finally(() => {
              setSubmitted(true)
              setAccountConfig({ [formName]: null })
              setAdvertiserPresetConfig({ [formName]: null })
              setFillingTypeConfig({ [formName]: null })
            })
        : new Promise((resolve, reject) =>
            dispatch({
              meta: { resolve, reject },
              type: CREATE_NEW_ACCOUNT,
              payload: {
                data: pick(values, registerFieldsNames),
                formName,
              },
            }),
          ).then(() => {
            setAccountConfig({ [formName]: null })
            setAdvertiserPresetConfig({ [formName]: null })
            setFillingTypeConfig({ [formName]: null })

            onClose({ confirm: false })
          
          sendMessageToYandex()
          })
    },
    [formName, onClose, multiLogins, registerFieldsNames],
  )

  submitData.submitted = submitted

  return submitData
}
